import React, { useState, useEffect } from 'react';
import { FaBell, FaUserCircle, FaSignOutAlt, FaBars } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { getToken, clearToken, checkTokenAndRedirect } from '../../../utils/tokenManager';
import './Topbar.css';

const Topbar = ({ toggleSidebar }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [showDropdown, setShowDropdown] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [userProfile, setUserProfile] = useState(null);
    const baseUrl = "https://api.burrayhr.com";

    // Fetch profile and notifications initially
    useEffect(() => {
        // Check token validity on component mount
        if (!checkTokenAndRedirect(navigate)) {
            return;
        }
        fetchProfile();
        fetchNotifications();

        // Set up intervals
        const notificationInterval = setInterval(fetchNotifications, 30000);
        const tokenCheckInterval = setInterval(() => {
            if (!checkTokenAndRedirect(navigate)) {
                clearInterval(tokenCheckInterval);
                clearInterval(notificationInterval);
            }
        }, 60000);

        // Cleanup intervals on component unmount
        return () => {
            clearInterval(notificationInterval);
            clearInterval(tokenCheckInterval);
        };
    }, [navigate]);

    // Check first login and redirect if necessary
    useEffect(() => {
        if (userProfile?.first_login) {
            const currentPath = location.pathname;
            if (currentPath !== '/employee/change-password') {
                Swal.fire({
                    icon: 'warning',
                    title: 'Change Password Required',
                    text: 'You must change your password before accessing other pages.',
                    confirmButtonColor: '#1e3a8a',
                    allowOutsideClick: false,
                    allowEscapeKey: false
                }).then(() => {
                    navigate('/employee/change-password');
                });
            }
        }
    }, [userProfile, location.pathname, navigate]);

    const fetchProfile = async () => {
        try {
            const token = getToken();
            if (!token) {
                navigate('/');
                return;
            }

            const response = await axios.get(`${baseUrl}/api/profile/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            console.log("user profile", response.data);
            setUserProfile(response.data);

            // Check first login immediately after fetching profile
            if (response.data?.first_login) {
                const currentPath = location.pathname;
                if (currentPath !== '/employee/change-password') {
                    Swal.fire({
                        icon: 'warning',
                        title: 'Change Password Required',
                        text: 'You must change your password before accessing other pages.',
                        confirmButtonColor: '#1e3a8a',
                        allowOutsideClick: false,
                        allowEscapeKey: false
                    }).then(() => {
                        navigate('/employee/change-password');
                    });
                }
            }

            // Store the user's name in localStorage for persistence
            localStorage.setItem('userName', `${response.data.first_name} ${response.data.last_name}`);
        } catch (error) {
            console.error('Error fetching profile:', error);
            if (error.response?.status === 401) {
                clearToken();
                navigate('/login');
            }
        }
    };

    const fetchNotifications = async () => {
        try {
            const token = getToken();
            if (!token) {
                navigate('/login');
                return;
            }

            const response = await axios.get(`${baseUrl}/api/notices`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            // Filter unread notifications
            const unreadNotifications = response.data.filter(notice => notice.is_read_by_current_user === false);
            setNotifications(response.data);
            setUnreadCount(unreadNotifications.length);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        }
    };

    const handleLogout = () => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You will be logged out of the system",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#2563eb',
            cancelButtonColor: '#95a5a6',
            confirmButtonText: 'Yes, logout'
        }).then((result) => {
            if (result.isConfirmed) {
                clearToken();
                navigate('/login');
            }
        });
    };

    // Prevent dropdown menu from showing if it's first login
    const handleDropdownClick = () => {
        if (userProfile?.first_login) {
            Swal.fire({
                icon: 'warning',
                title: 'Change Password Required',
                text: 'You must change your password before accessing other features.',
                confirmButtonColor: '#1e3a8a'
            });
            return;
        }
        setShowDropdown(!showDropdown);
    };

    const handleNotificationClick = () => {
        if (userProfile?.first_login) {
            Swal.fire({
                icon: 'warning',
                title: 'Change Password Required',
                text: 'You must change your password before accessing other features.',
                confirmButtonColor: '#1e3a8a'
            });
            return;
        }
        navigate('/employee/notices');
    };

    return (
        <div className="topbar">
            <div className="topbar-left">
                {/* Only show menu button if not first login */}
                {!userProfile?.first_login && (
                    <button className="menu-btn" onClick={toggleSidebar} aria-label="Toggle Menu">
                        <FaBars style={{fontSize: '2rem'}} className="menu-icon" />
                    </button>
                )}
                <h2 style={{marginLeft: userProfile?.first_login ? '20px' : '50px'}}>
                    {userProfile?.company_data?.name}
                </h2>
            </div>
            
            <div className="topbar-right">
                {/* Only show notification icon if not first login */}
                {!userProfile?.first_login && (
                    <div className="notification-icon" onClick={handleNotificationClick}>
                        <FaBell />
                        {unreadCount > 0 && (
                            <span className="notification-badge">{unreadCount}</span>
                        )}
                    </div>
                )}
                
                <div className="user-profile" onClick={handleDropdownClick}>
                    {userProfile?.employee_data?.photo ? (
                        <img 
                            src={userProfile.employee_data.photo} 
                            alt="Profile" 
                            className="avatar-image"
                            style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '50%',
                                objectFit: 'cover'
                            }}
                        />
                    ) : (
                        <FaUserCircle className="avatar" />
                    )}
                    <span className="username">
                        {userProfile ? `${userProfile.employee_data.full_name}` : 'Loading...'}
                    </span>
                    
                    {showDropdown && !userProfile?.first_login && (
                        <div className="dropdown-menu">
                            <div className="dropdown-header">
                                {userProfile?.employee_data?.photo ? (
                                    <img 
                                        src={userProfile.employee_data.photo} 
                                        alt="Profile" 
                                        className="dropdown-avatar-image"
                                        style={{
                                            width: '40px',
                                            height: '40px',
                                            borderRadius: '50%',
                                            objectFit: 'cover'
                                        }}
                                    />
                                ) : (
                                    <FaUserCircle className="dropdown-avatar" />
                                )}
                                <div className="user-info">
                                    <span className="user-name">
                                        {userProfile ? `${userProfile.employee_data.full_name}` : 'Loading...'}
                                    </span>
                                    <span className="user-role">{userProfile?.employee_data?.position || 'Employee'}</span>
                                    <span className="user-email">{userProfile?.employee_data?.email}</span>
                                </div>
                            </div>
                            <div className="dropdown-divider"></div>
                            <button 
                                className="dropdown-item" 
                                onClick={() => navigate('/employee/profile')}
                            >
                                <FaUserCircle />
                                <span>Profile</span>
                            </button>
                            <button className="dropdown-item" onClick={handleLogout}>
                                <FaSignOutAlt />
                                <span>Logout</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Topbar;
